/**
 * SDK v2.1.5 event formatter
 * Uses NESTED gift format: {gift: {name, emoji, value, count, totalValue}}
 */

/**
 * Format normalized event for SDK v2.1.5 consumption
 * @param {object} normalizedEvent - Normalized event from parser
 * @returns {object} SDK-formatted event
 */
function formatForSDK(normalizedEvent) {
  const { type, platform, user, ...rest } = normalizedEvent;
  
  switch (type) {
    case 'comment':
    case 'chat':
      return {
        type: 'stream-chat',
        data: {
          user: formatUser(user, platform),
          message: rest.message || rest.text || '',
          messageId: rest.id || generateId(),
          platform,
          timestamp: rest.timestamp || Date.now()
        }
      };
      
    case 'gift':
      // SDK 2.1.5: NESTED format (gift object)
      return {
        type: 'stream-gift',
        data: {
          user: formatUser(user, platform),
          gift: {
            id: rest.id || generateId(),
            name: rest.giftName || 'Gift',
            emoji: getGiftEmoji(rest.giftName),
            value: rest.value || 0,
            count: rest.giftCount || 1,
            totalValue: rest.totalValue || (rest.value || 0) * (rest.giftCount || 1)
          },
          platform,
          timestamp: rest.timestamp || Date.now()
        }
      };
      
    case 'like':
      return {
        type: 'stream-like',
        data: {
          user: formatUser(user, platform),
          count: rest.likeCount || rest.count || 1,
          platform,
          timestamp: rest.timestamp || Date.now()
        }
      };
      
    case 'join':
      return {
        type: 'stream-join',
        data: {
          user: formatUser(user, platform),
          platform,
          timestamp: rest.timestamp || Date.now()
        }
      };
      
    case 'follow':
      return {
        type: 'stream-follow',
        data: {
          user: formatUser(user, platform),
          platform,
          timestamp: rest.timestamp || Date.now()
        }
      };
      
    case 'share':
      return {
        type: 'stream-share',
        data: {
          user: formatUser(user, platform),
          platform,
          timestamp: rest.timestamp || Date.now()
        }
      };
      
    case 'stats':
      return {
        type: 'stream-stats',
        data: normalizedEvent.data // StreamMetrics object
      };
      
    default:
      throw new Error(`Unknown event type: ${type}`);
  }
}

/**
 * Format user object for SDK
 * @param {object} user - Normalized user
 * @param {string} platform - Platform name
 * @returns {object} SDK user format
 */
function formatUser(user, platform) {
  return {
    id: `${platform}_${user.username}`,
    username: user.username,
    displayName: user.displayName || user.username,
    platform,
    imageUrl: user.imageUrl || null
  };
}

/**
 * Generate unique ID
 * @returns {string}
 */
function generateId() {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Get emoji for gift name
 * @param {string} giftName - Gift name
 * @returns {string} Emoji
 */
function getGiftEmoji(giftName) {
  if (!giftName) return '🎁';
  
  const name = giftName.toLowerCase();
  
  // Common gift emojis
  if (name.includes('rose')) return '🌹';
  if (name.includes('heart')) return '❤️';
  if (name.includes('diamond')) return '💎';
  if (name.includes('star')) return '⭐';
  if (name.includes('crown')) return '👑';
  if (name.includes('fire')) return '🔥';
  if (name.includes('rocket')) return '🚀';
  if (name.includes('money') || name.includes('coin')) return '💰';
  if (name.includes('trophy')) return '🏆';
  if (name.includes('super chat')) return '💬';
  
  // Default
  return '🎁';
}

export { formatForSDK, formatUser, getGiftEmoji };
